import cn from "@/v2-ui/utils/utils.cn"
import type {
  PublicCategoryStatus,
  PublicPlatform,
} from "@/v2-status-client/status/status.types"
import StatusCategoryRow from "@/v2-status-client/status/category/StatusCategoryRow"
import StatusSummaryRow from "@/v2-status-client/status/summary/StatusSummaryRow"

export type StatusPlatformGroupProps = {
  platform: PublicPlatform
  categories: PublicCategoryStatus[]
}

// Overview platform card. Shown in the "All platforms" view where
// every platform is rendered side-by-side; the goal there is fast
// scanning ("is anything red?") so the layout stays compact: a
// single header row plus one summary row per category, and no
// services. The detailed per-platform view is rendered by
// StatusPlatformDetail instead.
//
// We render even when the platform has zero categories (e.g. the
// monitor hasn't probed anything for a fresh platform yet) so the
// page shape stays predictable across environments.
function StatusPlatformGroup(props: Readonly<StatusPlatformGroupProps>) {
  const { platform, categories } = props
  return (
    <article
      id={`platform-${platform.id}`}
      className={cn(
        "rounded-lg border border-border/60 bg-background/30",
        "overflow-hidden scroll-mt-24",
      )}
      aria-label={platform.name}
    >
      <StatusSummaryRow
        as="header"
        name={platform.name}
        description={platform.description}
        status={platform.status}
        summary={platform.summary}
        uptimePercent30d={platform.uptimePercent30d ?? null}
        className="border-b border-border/60 bg-muted/30 px-4 py-3 sm:px-5"
      />
      {categories.length > 0 ? (
        <ul className="m-0 flex list-none flex-col p-0 px-4 sm:px-5">
          {categories.map((c) => (
            <StatusCategoryRow key={c.id} category={c} />
          ))}
        </ul>
      ) : (
        <div className="px-4 py-3 text-xs text-muted-foreground sm:px-5">
          No services configured yet.
        </div>
      )}
    </article>
  )
}

export default StatusPlatformGroup
